// src/pages/orders/Orders.tsx
//
// Orders page (Admin/Partner)
// - Uses OrdersSearchPanel + OrdersTable.
// - URL query params are the single source of truth for *applied* filters and page (shareable deep links).
// - Uses draft vs applied state to avoid filtering while typing.
// - Auto-search behavior is owned by SearchPanel via `autoSearch` prop (do not implement debounce here).
// - Partner users are always scoped to their own partnerId.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { utcToZonedTime } from 'date-fns-tz';

import { OrderStatus } from '@models/order';
import { UserGroup } from '@models/user';
import { ProductSaleType } from '@apis/product';
import { exportOrderEsims, exportOrders, type GetOrdersParams } from '@apis/order';

import { useOrderList } from '@queries/orders/useOrderList';
import { useOrdersCount } from '@queries/orders/useOrdersCount';
import { useAllStockList } from '@queries/stocks/useAllStockList';
import { useAllPartnerList } from '@queries/partners/useAllPartnerList';
import { usePartnerDetail } from '@queries/partners/usePartnerDetail';

import useAsyncEffect from '@hooks/useAsyncEffect';
import { userState } from '@recoils/atoms';

import type { SelectValue } from '@components/common/Select';
import OrdersSearchPanel from '@components/order/OrdersSearchPanel';
import OrdersTable from '@components/order/OrdersTable';

import { downloadBlob } from '@utils/file';
import { filterSearchParams } from '@utils/search';
import { KOREAN_TIME_ZONE } from '@constants/timezone';

type OrdersSearchForm = GetOrdersParams;

const PAGE_SIZE = 50;
const PAGE_AUTO_SEARCH = { enabled: false, delayMs: 500 } as const;

const parseNumberParam = (value: string | null): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const parsePositiveInt = (value: string | null, fallback = 1) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

/**
 * Normalize a form for API/URL:
 * - Trim strings
 * - Drop empty strings
 * - Keep numbers/enums as-is
 */
const cleanSearchForm = (form: OrdersSearchForm): OrdersSearchForm => {
  const cleaned: Record<string, unknown> = {};

  Object.entries(form).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return;
      cleaned[key] = trimmed;
      return;
    }

    cleaned[key] = value;
  });

  return cleaned as OrdersSearchForm;
};

const buildExportFileName = (prefix: string) => {
  const kstDate = utcToZonedTime(new Date(), KOREAN_TIME_ZONE);
  return `${prefix}-${kstDate.toISOString()}.xlsx`;
};

const readSearchFromUrl = (sp: URLSearchParams, defaults: OrdersSearchForm): OrdersSearchForm => {
  return {
    ...defaults,
    orderCode: sp.get('orderCode') || '',
    productCode: sp.get('productCode') || '',
    productSaleType: (sp.get('productSaleType') as ProductSaleType) || undefined,
    productName: sp.get('productName') || '',
    stockId: parseNumberParam(sp.get('stockId')),
    partnerId: sp.get('partnerId') ? Number(sp.get('partnerId')) : (defaults.partnerId ?? undefined),
    orderStatus: (sp.get('orderStatus') as OrderStatus) || undefined,
    createdAtFrom: parseNumberParam(sp.get('createdAtFrom')),
    createdAtTo: parseNumberParam(sp.get('createdAtTo')),
    reservedAtFrom: parseNumberParam(sp.get('reservedAtFrom')),
    reservedAtTo: parseNumberParam(sp.get('reservedAtTo')),
    iccid: sp.get('iccid') ?? undefined,
    passportNumber: sp.get('passportNumber') ?? undefined,
    requestOrderId: sp.get('requestOrderId') || undefined,
    externalOrderId: sp.get('externalOrderId') || undefined,
  };
};

const buildUrlParams = (form: OrdersSearchForm, page: number) => {
  const cleaned = cleanSearchForm(form);

  const entries = Object.entries(cleaned).reduce<Record<string, string>>((acc, [k, v]) => {
    if (v !== undefined && v !== null) acc[k] = String(v);
    return acc;
  }, {});

  return {
    ...filterSearchParams(Object.fromEntries(new URLSearchParams(entries).entries())),
    page: String(page),
  };
};

const Orders = () => {
  const user = useRecoilValue(userState);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: partnerDetailForm } = usePartnerDetail(Number(user?.partnerId));

  /* ------------------------------------------------------------------ */
  /* Defaults                                                            */
  /* ------------------------------------------------------------------ */

  const buildDefaultForm = useCallback((): OrdersSearchForm => {
    return {
      orderCode: '',
      productCode: '',
      productSaleType: undefined,
      productName: '',
      stockId: undefined,
      partnerId: user?.partnerId ? user.partnerId : undefined,
      orderStatus: undefined,
      createdAtFrom: undefined,
      createdAtTo: undefined,
      reservedAtFrom: undefined,
      reservedAtTo: undefined,
      iccid: undefined,
      passportNumber: undefined,
      requestOrderId: undefined,
      externalOrderId: undefined,
    };
  }, [user?.partnerId]);

  /* ------------------------------------------------------------------ */
  /* State: draft vs applied + page                                      */
  /* ------------------------------------------------------------------ */

  const [draftSearch, setDraftSearch] = useState<OrdersSearchForm>(() => {
    const defaults = buildDefaultForm();
    return readSearchFromUrl(searchParams, defaults);
  });

  const [appliedSearch, setAppliedSearch] = useState<OrdersSearchForm>(() => cleanSearchForm(draftSearch));

  const [page, setPage] = useState<number>(() => parsePositiveInt(searchParams.get('page'), 1));

  /**
   * Keep local state in sync with URL changes
   * (deep link / back-forward / manual URL edit).
   */
  useEffect(() => {
    const defaults = buildDefaultForm();
    const nextDraft = readSearchFromUrl(searchParams, defaults);
    const nextApplied = cleanSearchForm(nextDraft);
    const nextPage = parsePositiveInt(searchParams.get('page'), 1);

    setDraftSearch(nextDraft);
    setAppliedSearch(nextApplied);
    setPage(nextPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, buildDefaultForm]);

  /**
   * Partner users must always be scoped to their own partnerId.
   * We enforce it in both draft/applied and re-sync URL.
   */
  useEffect(() => {
    if (!user?.partnerId) return;

    setDraftSearch((prev) => ({ ...prev, partnerId: user.partnerId }));
    setAppliedSearch((prev) => ({ ...prev, partnerId: user.partnerId }));

    // If URL was manually edited, force partnerId back.
    const current = readSearchFromUrl(searchParams, buildDefaultForm());
    if (current.partnerId !== user.partnerId) {
      const patched = { ...current, partnerId: user.partnerId };
      setSearchParams(buildUrlParams(patched, page));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.partnerId]);

  /* ------------------------------------------------------------------ */
  /* URL sync helper (applied + page)                                     */
  /* ------------------------------------------------------------------ */

  const syncUrlFromApplied = useCallback(
    (nextApplied: OrdersSearchForm, nextPage: number) => {
      setSearchParams(buildUrlParams(nextApplied, nextPage));
    },
    [setSearchParams],
  );

  /* ------------------------------------------------------------------ */
  /* Draft setter (SearchPanel contract: null means clear)               */
  /* ------------------------------------------------------------------ */

  const handleChangeInput = useCallback((name: keyof OrdersSearchForm, value: string | number | null) => {
    setDraftSearch((prev) => ({ ...prev, [name]: value ?? undefined }));
  }, []);

  /* ------------------------------------------------------------------ */
  /* Apply / Reset                                                       */
  /* ------------------------------------------------------------------ */

  const handleOnSearch = useCallback(() => {
    const nextApplied = cleanSearchForm(draftSearch);

    // Partner safety: always keep partnerId.
    if (user?.group === UserGroup.PARTNER && user.partnerId) {
      nextApplied.partnerId = user.partnerId;
    }

    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [draftSearch, syncUrlFromApplied, user?.group, user?.partnerId]);

  const handleOnReset = useCallback(() => {
    const nextDraft = buildDefaultForm();
    setDraftSearch(nextDraft);

    const nextApplied = cleanSearchForm(nextDraft);
    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [buildDefaultForm, syncUrlFromApplied]);

  /* ------------------------------------------------------------------ */
  /* Queries (driven by applied + page)                                  */
  /* ------------------------------------------------------------------ */

  const cleanedApplied = useMemo(() => cleanSearchForm(appliedSearch), [appliedSearch]);

  const { data, isLoading } = useOrderList({
    page,
    searchParam: cleanedApplied,
    user,
  });

  const { data: totalCount, isLoading: isLoadingCount } = useOrdersCount(cleanedApplied, user);

  /* ------------------------------------------------------------------ */
  /* Paging                                                              */
  /* ------------------------------------------------------------------ */

  const handleChangePage = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
      syncUrlFromApplied(appliedSearch, value);
    },
    [appliedSearch, syncUrlFromApplied],
  );

  /* ------------------------------------------------------------------ */
  /* Export (based on applied)                                           */
  /* ------------------------------------------------------------------ */

  const [isExporting, setExporting] = useState(false);

  const handleOnExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await exportOrders(cleanedApplied);
      downloadBlob(res, buildExportFileName('orders'));
    } finally {
      setExporting(false);
    }
  }, [cleanedApplied]);

  const handleOnExportReport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await exportOrderEsims(cleanedApplied);
      downloadBlob(res, buildExportFileName('orders-report'));
    } finally {
      setExporting(false);
    }
  }, [cleanedApplied]);

  /* ------------------------------------------------------------------ */
  /* Dropdown options (admin only)                                       */
  /* ------------------------------------------------------------------ */

  const [stockOptionList, setStockOptionList] = useState<{ value: SelectValue; label: string }[]>([]);
  const [partnerOptionList, setPartnerOptionList] = useState<{ value: SelectValue; label: string }[]>([]);

  const shouldLoadAdminOptions = user?.group !== UserGroup.PARTNER;
  const { data: stocksData } = useAllStockList(shouldLoadAdminOptions);
  const { data: partnersData } = useAllPartnerList(shouldLoadAdminOptions);

  useAsyncEffect(async () => {
    if (!stocksData) return;
    setStockOptionList(stocksData.data.map((s) => ({ label: s.stockCode, value: s.stockId })));
  }, [stocksData]);

  useAsyncEffect(async () => {
    // Partner user: force a single option (their own partner).
    if (user?.group === UserGroup.PARTNER) {
      setPartnerOptionList([
        { label: partnerDetailForm?.data?.[0]?.name || '', value: user?.partnerId || 0 },
      ]);
      return;
    }

    if (!partnersData) return;
    setPartnerOptionList(partnersData.data.map((p) => ({ label: p.name, value: p.partnerId })));
  }, [partnersData, partnerDetailForm, user?.group, user?.partnerId]);

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */

  const isTableLoading = Boolean(isLoading || isLoadingCount);

  return (
    <Box>
      <OrdersSearchPanel
        searchForm={draftSearch}
        onChangeInput={handleChangeInput}
        onSearch={handleOnSearch}
        onReset={handleOnReset}
        onExport={handleOnExport}
        onExportReport={handleOnExportReport}
        onClickCreate={user?.manualOrderEnabled ? () => navigate('/orders/add') : undefined}
        stockOptions={stockOptionList}
        partnerOptions={partnerOptionList}
        user={user}
        isExporting={isExporting}
        autoSearch={PAGE_AUTO_SEARCH}
      />

      <OrdersTable
        data={data?.data || []}
        isLoading={isTableLoading}
        totalCount={totalCount}
        page={page}
        pageSize={PAGE_SIZE}
        onChangePage={handleChangePage}
        user={user}
      />
    </Box>
  );
};

export default Orders;
